import React, { useState, useEffect, ReactNode, useMemo, useCallback } from 'react';
import { ApiResponse, AspNetRolesMenuItemsPermissions, AspNetRolesMenuItemsPermissions_EXT, Items_EXT } from '../../Models/Classes';
import { useAxios } from '../../Helpers/AxiosHelper';
import AuthService from '../Authentication/AuthService';
import { PermissionContext, LoadingState } from './PermissionContextValue';
import { useHasPermission } from './PermissionHooks';

interface PermissionHelperProps {
    requiredPermission: AspNetRolesMenuItemsPermissions;
    children: React.ReactNode;
    fallback?: React.ReactNode;
}


export const PermissionHelper: React.FC<PermissionHelperProps> = ({ requiredPermission, children, fallback = null }) => {
    const hasPermission = useHasPermission(requiredPermission);
    return hasPermission ? <>{children}</> : <>{fallback}</>;
};

export const PermissionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [permissions, setPermissions] = useState<AspNetRolesMenuItemsPermissions_EXT[]>([]);
    const [items, setItems] = useState<Items_EXT[]>([]);
    const [userRole, setUserRole] = useState<string>('');
    const [loading, setLoading] = useState<LoadingState>({ items: true, permissions: true, userRole: true });
    const [errors, setErrors] = useState<Partial<LoadingState>>({});
    const [pageID, setPageID] = useState<string>('');
    const AxiosHelper = useAxios();

    const updateState = useCallback((loadingKey: keyof LoadingState, isLoading: boolean, errorKey: keyof LoadingState, hasError: boolean) => {
        setLoading(prev => ({ ...prev, [loadingKey]: isLoading }));
        setErrors(prev => ({ ...prev, [errorKey]: hasError }));
    }, []);

    const fetchData = useCallback(async <T,>(endpoint: string, setter: (value: T) => void, loadingKey: keyof LoadingState, errorKey: keyof LoadingState, controller: AbortController) => {
        updateState(loadingKey, true, errorKey, false);
        try {
            const response = await AxiosHelper.get<ApiResponse<T>>(endpoint, { signal: controller.signal });
            if (response.data.code === 0) {
                setter(response.data.data);
            } else {
                throw new Error(response.data.message);
            }
        } catch (error) {
            if (controller.signal.aborted) {
                console.warn(`Fetch aborted for ${endpoint}`);
            } else {
                updateState(loadingKey, false, errorKey, true);
                console.error(`Failed to fetch ${endpoint}:`, error);
            }
        } finally {
            updateState(loadingKey, false, errorKey, false);
        }
    }, [AxiosHelper, updateState]);

    const initiateFetch = useCallback(<T,>(endpoint: string, setter: (value: T) => void, loadingKey: keyof LoadingState, errorKey: keyof LoadingState) => {
        const controller = new AbortController();
        fetchData(endpoint, setter, loadingKey, errorKey, controller);
        return () => controller.abort();
    }, [fetchData]);

    useEffect(() => {
        const userEmail = AuthService.getCurrentUser()?.UserInfo?.email;
        if (!userEmail) return;

        return initiateFetch(`/Auth/GetPermissions/${userEmail}`, setPermissions, 'permissions', 'permissions');
    }, [initiateFetch]);

    useEffect(() => {
        return initiateFetch('/Auth/GetItems', setItems, 'items', 'items');
    }, [initiateFetch]);

    useEffect(() => {
        const userEmail = AuthService.getCurrentUser()?.UserInfo?.email;
        if (!userEmail) return;

        return initiateFetch(`/Auth/GetRoles/${userEmail}`, setUserRole, 'userRole', 'userRole');
    }, [initiateFetch]);

    const contextValue = useMemo(() => ({
        permissions,
        items,
        userRole,
        loading,
        errors,
        pageID,
        setPageID,
        setPermissions,
        setItems,
        setUserRole
    }), [permissions, items, userRole, loading, errors, pageID]);

    return (
        <PermissionContext.Provider value={contextValue}>
            {children}
        </PermissionContext.Provider>
    );
};
